import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { makeStyles, useTheme } from '@mui/styles';
import PropTypes from 'prop-types';
import * as topojson from 'topojson-client';
import clsx from 'clsx';

import LinearProgress from '@mui/material/LinearProgress';
import Popover from '@mui/material/Popover';
import StyledPaper from './../styled/paper';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import SearchIcon from '@mui/icons-material/Search';

import {
    geoGraticule as D3geoGraticule,
    scaleLinear as D3scaleLinear,
    select as D3select,
} from 'd3';
import Moment from 'moment';

import D3Geomap from '../../d3/D3Geomap';
import { yellow } from '../../data/colors';
import countriesJson from '../../../topojson/ne_110m_countries.json';
import placesJson from '../../../topojson/ne_110m_places.json';

const useStyles = makeStyles(theme => ({
    root: {
        '& .feature': {
            '&.countries': {
                filter: 'url(#glow)',
                fill: theme.palette.info.light,
                stroke: theme.palette.grey[800],
                opacity: 0.2,
            },
            '&.graticule': {
                stroke: theme.palette.grey[800],
                strokeWidth: 0.25,
                fill: 'none',
            },
            '&.rivers': {
                stroke: theme.palette.info.light,
                strokeWidth: 0.25,
                fill: 'none',
            },
            '&.lakes': {
                strokeWidth: 0.25,
                fill: theme.palette.info.light,
            },
        },

        '& .feature-label': {
            textAnchor: 'middle',
            '&.country-labels': {
                fill: theme.palette.text.secondary,
                fillOpacity: 0.5,
                fontWeight: 300,
                fontSize: 12,
            },
            '&.places': {
                fill: theme.palette.text.secondary,
                fontSize: 10,
            },
        },

        '& .node': {
            opacity: 1,
            '&.bkgd': {
                opacity: 0.2,
            },
        },

        // pass theme colors to buttons
        '& .zbtn': {
            stroke: theme.palette.info.light,
            fill: theme.styled.paper.background,

            '& path': {
                fill: theme.palette.info.light,
            }
        },

        // animated path styles
        '& .brcanimatedpath path.animated': {
            opacity: 1,
        },
        '& .bkgnd .brcanimatedpath path.animated': {
            opacity: 0.2,
        },
        '& .frgnd .brcanimatedpath path.animated': {
            strokeWidth: 6,
        },

        '&.bt': {
            '& .feature': {
                '&.countries': {
                    fill: theme.palette.backtesting.main,
                },
                '&.rivers': {
                    stroke: theme.palette.backtesting.main,
                },
                '&.lakes': {
                    fill: theme.palette.backtesting.main,
                },
            },
            '& .zbtn': {
                stroke: theme.palette.backtesting.main,
                '& path': {
                    fill: theme.palette.backtesting.main,
                }
            },
        },
    },
    refresh: {
        textShadow: `0 0 10px ${theme.palette.primary.contrastText}, 0 0 20px ${yellow[400]}`,
    },
    asof: {
        fontSize: 12,
        marginLeft: 10,
        transition: '500ms',
        transitionProperty: 'text-shadow, font-weight',
    },
    progress: {
        width: '100%',
        // reverse progressbar
        '& .MuiLinearProgress-colorSecondary': {
            backgroundColor: theme.palette.secondary.main,
            '& .MuiLinearProgress-barColorSecondary': {
                backgroundColor: theme.palette.secondary.dark,
            }
        }
    },
    itemIcon: {
        minWidth: 25,
    },
}));

const GeoMap = (props) => {
    const d3Container = useRef(null);
    const classes = useStyles();
    const theme = useTheme();
    const [refreshed, setRefreshed] = useState(false);
    const [progress, setProgress] = useState();
    const [showProgress, setShowProgress] = useState(false);
    const [currentPopup, setCurrentPopup] = useState();
    const [selections, setSelections] = useState({});

    const world = useMemo(() => {
        return D3Geomap()
            .topology('countries', true, topojson.feature(countriesJson, countriesJson.objects.countries))
            .label('country-labels', true, topojson.feature(countriesJson, countriesJson.objects.countries))
            .topology('graticule', true, D3geoGraticule()())
            .label('places', true, topojson.feature(placesJson, placesJson.objects.places))
            .colors(theme.GeoNodeColors);
    }, [theme.GeoNodeColors]);

    const handleClose = () => {
        setCurrentPopup();
    };

    const handleOnCick = useCallback((ips, node, key) => {
        const pin = selections[key] !== 'pinned';
        if (!ips) setSelections({});
        else setCurrentPopup({ ips, elm: node[0], pin, key });
    }, [selections]);

    const handlePin = useCallback(() => {
        if (!currentPopup) return;
        setSelections(current => {
            if (currentPopup.pin) current[currentPopup.key] = 'pinned';
            else delete current[currentPopup.key];
            return { ...current };
        });
        setCurrentPopup();
    }, [currentPopup]);

    const handleFilterSrc = useCallback(() => {
        if (!currentPopup) return;
        props.onFilterSrc && props.onFilterSrc(currentPopup.ips);
        setCurrentPopup();
    }, [currentPopup, props]);

    const handleFilterDest = useCallback(() => {
        if (!currentPopup) return;
        props.onFilterDest && props.onFilterDest(currentPopup.ips);
        setCurrentPopup();
    }, [currentPopup, props]);

    useEffect(() => {
        if (progress === undefined) return;
        // let the progressbar get down to zero before removing for happy ending
        if (progress === 0) setTimeout(() => setShowProgress(false), 500);
        else setShowProgress(true);
    }, [progress]);

    useEffect(() => {
        // don't update history backfilling progress based on live data
        if (!props.data || !props.data.start || !props.data.end || props.data.live) return;

        // figure out progress bar
        const scale = D3scaleLinear()
            .domain([Moment(props.data.start).unix(), Moment(props.data.end).unix()])
            .range([0, 100]);
        const prog = scale(Moment(props.data.next_page || props.data.start).unix());
        setProgress(prog);
    }, [props.data]);

    // gui indicator when 'asof' was updated
    useEffect(() => {
        setRefreshed(true);
        setTimeout(() => setRefreshed(false), 2000);
    }, [props.data.end]);

    useEffect(() => {
        setRefreshed(true);
        setTimeout(() => setRefreshed(false), 2000);
    }, [props.heartbeat]);

    useEffect(() => {
        if (d3Container.current && props.data) {
            if (props.data.live) console.debug('getting live data', props.data);
            if (props.data.next_page) console.debug('backfilling', props.data);
            const node = d3Container.current;

            const margin = 30;
            const w = props.size.width - 2 * margin;
            const h = props.size.height > 500
                ? props.size.height - 2 * margin
                : Math.min(w * 0.6, props.size.height - 2 * margin);
            const mapdiv = D3select(node).data(['div']).join('div');

            const mapg = mapdiv
                .selectAll('.geomap')
                .data([{ ...props.data, ...{ key: 'mtdt' } }], d => d.key);
            mapg.enter()
                .append('svg')
                .attr('class', 'geomap')
                .style('display', 'block')
                .style('margin', 'auto')
                .merge(mapg)
                .call(world
                    .colors(theme.GeoNodeColors)
                    .theme(theme)
                    .width(w)
                    .wheight(h - 10)
                    .clear(props.data.clear)
                    .live(props.data.live)
                    .cselection(selections)
                    .on('click', o => { handleOnCick(o.ips, o.elt, o.key) }));
        }
    }, [theme, props.data, props.size, world, selections, handleOnCick]);

    return (
        <React.Fragment>
            <div className={clsx(classes.root, {
                bt: props.isBT,
            })} ref={d3Container}></div>

            <span className={clsx(classes.asof, refreshed && classes.refresh)}>
                {`Last activity seen ${Moment(props.data.end).format(theme.date.long)}`}
            </span>

            { /* lazy map history inverse progressbar */ }
            {showProgress &&
                <div className={classes.progress}>
                    <LinearProgress color="secondary" variant="determinate" value={progress} />
                </div>
            }

            {currentPopup && <Popover
                open={Boolean(currentPopup)}
                anchorEl={currentPopup.elm}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}>
                <StyledPaper className={classes.paper}>
                    <MenuItem onClick={handlePin}>
                        <ListItemIcon className={classes.itemIcon}>
                            {currentPopup.pin
                                ? <LocationOnIcon fontSize="small" />
                                : <LocationOffIcon fontSize="small" />
                            }
                        </ListItemIcon>
                        <ListItemText primary={currentPopup.pin ? 'Pin' : 'Unpin'} />
                    </MenuItem>
                    <MenuItem onClick={handleFilterSrc}>
                        <ListItemIcon className={classes.itemIcon}>
                            <SearchIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={`Filter by ${props.srcStr}`} />
                    </MenuItem>
                    <MenuItem onClick={handleFilterDest}>
                        <ListItemIcon className={classes.itemIcon}>
                            <SearchIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={`Filter by ${props.destStr}`} />
                    </MenuItem>
                </StyledPaper>
            </Popover>}

        </React.Fragment>
    );
};

GeoMap.propTypes = {
    data: PropTypes.object,
    heartbeat: PropTypes.number,
    onFilterSrc: PropTypes.func,
    onFilterDest: PropTypes.func,
    srcStr: PropTypes.string,
    destStr: PropTypes.string,
    // header: PropTypes.number,
    size: PropTypes.object.isRequired,
    isBT: PropTypes.bool,
};

export default GeoMap;
